(function () {
  "use strict";

  const header = document.querySelector(".header");
  const headerLink = document.querySelector(".header__logo-link");
  const sidebarNav = document.querySelector(".sidebar-nav");
  const sidebarLinks = document.querySelectorAll(".sidebar-nav__link");
  const cartBtnDesktop = document.getElementById("cartBtnDesktop");
  const sections = document.querySelectorAll("section[id]");

  let currentActive = null;

  function setActiveLink(sectionId) {
    if (currentActive === sectionId) return;

    // Only update if this section has a matching nav link
    const hasNavLink = Array.from(sidebarLinks).some(
      (l) => l.getAttribute("data-section") === sectionId
    );
    if (!hasNavLink) return;

    currentActive = sectionId;
    sidebarLinks.forEach((link) => {
      const isActive = link.getAttribute("data-section") === sectionId;
      link.classList.toggle("is-active", isActive);
    });
  }

  function closestThemeNode(el) {
    let cur = el;
    while (cur && cur !== document.documentElement) {
      if (cur.dataset && (cur.dataset.nav || cur.dataset.theme)) return cur;
      cur = cur.parentElement;
    }
    return null;
  }

  function resolveThemeFromEl(el) {
    const themed = closestThemeNode(el);
    if (!themed) return "light";
    if (themed.dataset.nav === "force-white") return "force-white";
    if (themed.dataset.theme === "light") return "light";
    return "dark";
  }

  function underlyingElementAtPoint(x, y, ignoreContainer) {
    const stack = document.elementsFromPoint(x, y);
    for (const el of stack) {
      if (!ignoreContainer) return el;
      if (!ignoreContainer.contains(el)) return el;
    }
    return null;
  }

  function themeAtPoint(x, y, ignoreEl) {
    const under = underlyingElementAtPoint(x, y, ignoreEl);
    return resolveThemeFromEl(under);
  }

  // Header: compute a single top/bottom split region for the blue overlay
  function updateHeaderMask() {
    if (!header || !headerLink) return;

    const r = headerLink.getBoundingClientRect();

    // center x of logo; scan within its height
    const x = r.left + r.width / 2;
    const yTop = r.top + 1;
    const yBot = r.bottom - 1;

    const topTheme = themeAtPoint(x, yTop, header);
    const botTheme = themeAtPoint(x, yBot, header);

    // force-white anywhere => show only white (no blue overlay)
    if (topTheme === "force-white" || botTheme === "force-white") {
      header.style.setProperty("--logo-light-start", "0%");
      header.style.setProperty("--logo-light-end", "0%");
      return;
    }

    const topIsLight = topTheme === "light";
    const botIsLight = botTheme === "light";

    // Uniform: either full blue overlay (on light bg) or none (on dark bg)
    if (topIsLight && botIsLight) {
      header.style.setProperty("--logo-light-start", "0%");
      header.style.setProperty("--logo-light-end", "100%");
      return;
    }
    if (!topIsLight && !botIsLight) {
      header.style.setProperty("--logo-light-start", "0%");
      header.style.setProperty("--logo-light-end", "0%");
      return;
    }

    // Split: find boundary by binary search (assumes single boundary within logo height)
    let lo = yTop;
    let hi = yBot;
    const targetIsLightAtLo = topIsLight;

    for (let i = 0; i < 7; i++) {
      // 7 iters ~ sub-pixel accuracy for 44px
      const mid = (lo + hi) / 2;
      const midTheme = themeAtPoint(x, mid, header);
      const midIsLight = midTheme === "light";

      if (midIsLight === targetIsLightAtLo) {
        lo = mid;
      } else {
        hi = mid;
      }
    }

    const boundaryY = (lo + hi) / 2;
    const boundaryPct = ((boundaryY - r.top) / r.height) * 100;

    // If top is light, light region = [0, boundary]; else [boundary, 100]
    if (topIsLight) {
      header.style.setProperty("--logo-light-start", "0%");
      header.style.setProperty(
        "--logo-light-end",
        boundaryPct.toFixed(2) + "%",
      );
    } else {
      header.style.setProperty(
        "--logo-light-start",
        boundaryPct.toFixed(2) + "%",
      );
      header.style.setProperty("--logo-light-end", "100%");
    }
  }

  // Sidebar: per-link sampling so each item can flip independently
  function updateSidebarThemes() {
    if (!sidebarNav || !sidebarLinks.length) return;

    sidebarLinks.forEach((link) => {
      const r = link.getBoundingClientRect();
      const x = r.left + r.width / 2;
      const y = r.top + r.height / 2;

      const t = themeAtPoint(x, y, sidebarNav);
      // force-white behaves like dark (white text)
      link.setAttribute("data-over", t);
    });
  }

  function updateCartBtnTheme() {
    if (!cartBtnDesktop) return;
    const r = cartBtnDesktop.getBoundingClientRect();
    const x = r.left + r.width / 2;
    const y = r.top + r.height / 2;
    const t = themeAtPoint(x, y, header);
    cartBtnDesktop.setAttribute("data-over", t);
  }

  function updateOnScroll() {
    updateHeaderMask();
    updateSidebarThemes();
    updateCartBtnTheme();
  }

  // Intersection observer for active link highlighting (unchanged conceptually)
  const sectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveLink(entry.target.id);
      });
    },
    { root: null, rootMargin: "-50% 0px -50% 0px", threshold: 0 },
  );

  sections.forEach((s) => sectionObserver.observe(s));

  // Smooth scroll
  function handleNavClick(e) {
    const href = this.getAttribute("href");
    const targetId = href.replace(/^\/?(#)/, "").replace(/^#/, "");
    const target = document.getElementById(targetId);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: "smooth" });
    }
    // If no matching element on this page, let the browser navigate normally
  }
  sidebarLinks.forEach((l) => l.addEventListener("click", handleNavClick));

  // rAF throttle
  let ticking = false;
  function scheduleUpdate() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      updateOnScroll();
      ticking = false;
    });
  }

  window.addEventListener("scroll", scheduleUpdate, { passive: true });
  window.addEventListener("resize", scheduleUpdate);

  // Mark Music as active on first load — set directly so the observer
  // can still override it as the user scrolls, but won't fire on load
  // since the music section spans the top of the page.
  currentActive = 'music';
  sidebarLinks.forEach((link) => {
    link.classList.toggle('is-active', link.getAttribute('data-section') === 'music');
  });

  updateOnScroll();
})();
